import api from './api';
import { toast } from 'utils/toast';
import { operations } from '_types/api';
import { addData } from 'App/redux/appSlice';
import { addToList, singleSelection } from '_common/components/Table/TableSlice';
import { setExplorerFiles } from 'App/redux/onboardingSlice';
import { isOnboardingExplorerActive } from '_common/hooks/Onboarding';

type APIError = {
  error: { data: any; status: number };
};

export const ObjectApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getObject2: builder.query<
      ApiOperations['get_object']['responses']['200']['content']['application/json'],
      ApiOperations['get_object']['parameters']['path'] & {
        open?: boolean;
      }
    >({
      query: ({ object_id, object_type, open }) => {
        return {
          url: `/object/${object_type}/get/${object_id}`,
          errorsExpected: [403, 404],
          config: { params: { open } },
        };
      },
      providesTags: (result, error, arg) => [
        { type: arg.object_type, id: arg.object_id },
        { type: 'Object', id: arg.object_id },
      ],
    }),
    editObject: builder.mutation<
      ApiOperations['edit_document']['responses']['200'],
      ApiOperations['get_object']['parameters']['path'] &
        ApiOperations['edit_document']['requestBody']['content']['multipart/form-data']
    >({
      query: ({ object_id, object_type, description, name }) => ({
        url: `/object/${object_type}/${object_id}/edit`,
        method: 'POST',
        body: { name, description },
      }),
      invalidatesTags: (result, error, args) => [{ type: args.object_type, id: args.object_id }],
    }),
    listSpaces: builder.query<
      operations['list_spaces']['responses']['200']['content']['application/json'],
      Request.AllListParams
    >({
      query: (params) => ({
        url: `/object/space/list`,
        config: {
          params,
        },
      }),
      providesTags: (result, error, arg) =>
        result ? [...result.nodes.map(({ id }) => ({ type: 'Object' as const, id }))] : [],
    }),
    listSpaceDir: builder.query<
      ApiSchemas['DirSpaceResponseSchema'],
      { id: string; params: Request.AllListParams }
    >({
      query: ({ id, params }) => ({
        url: `/object/space/${id}/list`,
        config: {
          params,
        },
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.nodes.map(({ id }) => ({ type: 'Object' as const, id })),
              { type: 'Object', id: arg.id },
            ]
          : [{ type: 'Object', id: arg.id }],
    }),
    createSpace: builder.mutation<
      operations['create_space']['responses']['200'],
      { name: string; description: string }
    >({
      query: ({ name, description }) => {
        return {
          url: `/object/space/create`,
          method: 'POST',
          body: { name, description },
          errorsExpected: [400],
        };
      },
      invalidatesTags: () => ['Object'],
      onQueryStarted: async (args, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(addData({ [data.id]: data }));
          dispatch(addToList({ identity: 'spaces', objectId: data.id }));
          dispatch(singleSelection({ identity: 'spaces', objectId: data.id }));
          const onboardingExplorerIsActive = isOnboardingExplorerActive(getState() as RootState);
          if (onboardingExplorerIsActive) {
            dispatch(setExplorerFiles(data.id));
          }
          toast({
            variant: 'success',
            title: 'NEW_ELEMENT_TYPE_CREATED',
            titleValues: { elementType: data.type },
            message: 'NEW_ELEMENT_TYPE_SUCCESSFULLY_CREATED',
            messageValues: { elementType: data.type },
          });
        } catch (error) {
          // Same as RTK Thunk 'rejected' state
          const typedError = (error as APIError).error;
          if (typedError.status === 400) {
            toast({
              variant: 'error',
              title: 'INCORRECT_NAME',
              message: 'SPACE_INCORRECT_NAME_MESSAGE',
            });
          }
        }
      },
    }),
    listFolderDir: builder.query<ApiSchemas['DirFolderResponseSchema'], string>({
      query: (id) => ({
        url: `/api/object/folder/${id}/list`,
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.nodes.map(({ id }) => ({ type: 'Object' as const, id })),
              { type: 'Object', id: arg },
            ]
          : [{ type: 'Object', id: arg }],
    }),
    listAuditLog: builder.query<
      operations['list_actions']['responses']['200'],
      Request.AllListParams & { args: { objectType: 'document' | 'dopdf'; objectId: ObjectId } }
    >({
      query: (params) => {
        const apiParams: Partial<typeof params> = { ...params };
        delete apiParams.args;
        return {
          url: `/object/${params.args.objectType}/${params.args.objectId}/actions`,
          config: {
            params: apiParams,
          },
        };
      },
      providesTags: (result) =>
        //@ts-expect-error API is sending "_id" but API type is "id"
        result ? [...result.nodes.map(({ _id }) => ({ type: 'Object' as const, _id }))] : [],
    }),
    listRecycle: builder.query<
      operations['list_recycle']['responses']['200']['content']['application/json'],
      Request.AllListParams
    >({
      query: (params) => ({
        url: `/recycle/list`,
        config: {
          params,
        },
      }),
      providesTags: (result, error, arg) =>
        result ? [...result.nodes.map(({ id }) => ({ type: 'Object' as const, id }))] : [],
    }),
    restoreObject: builder.mutation<void, ObjectId>({
      query: (id) => ({
        url: `/recycle/${id}/restore`,
        method: 'POST',
        errorsExpected: [403, 404],
        body: { id },
      }),
      invalidatesTags: () => ['Object'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          toast({
            variant: 'success',
            title: 'global.success',
            message: 'storage.notifications.restore.messageSuccess',
          });

          // dispatch(removeFromList({identity: 'recycle', objectId: id}));
          // dispatch(clearSelection());
        } catch (e) {
          const error = (e as APIError).error;
          switch (error?.status) {
            case 403:
              toast({
                variant: 'error',
                title: 'global.error',
                message: 'storage.notifications.restore.insufficientPermissions',
              });
              break;
            case 404:
              toast({
                variant: 'error',
                title: 'global.error',
                message: 'storage.notifications.restore.missingOrignialLocation',
              });
              break;
            default:
              break;
          }
        }
      },
    }),
    getObjectPath: builder.query<ApiSchemas['PathSchema'], string>({
      query: (id) => ({ url: `/object/${id}/path` }),
      providesTags: (result, error, arg) => [{ type: 'path', id: arg }],
    }),
  }),
});

// Export queries
export const {
  useGetObject2Query,
  useEditObjectMutation,
  useListSpacesQuery,
  useListSpaceDirQuery,
  useListFolderDirQuery,
  useCreateSpaceMutation,
  useLazyListAuditLogQuery,
  useListRecycleQuery,
  useRestoreObjectMutation,
  useGetObjectPathQuery,
} = ObjectApi;

export default ObjectApi;
